export type ChartType = 'change' | 'bars' | 'stacked' | 'meter';

const MAX_ROWS = 20;
// A printed value is never cut, so a longer one sends the figure back to its table.
const MAX_VALUE_CHARS = 24;

// Languages that write 1.003 for a thousand and three, and 0,125 for an eighth.
const DECIMAL_COMMA = /^(fr|es|pt)(?:[-_]|$)/i;
const UNIT = /[\p{L}%]+$/u;

interface Measure {
  value: number;
  unit: string;
}

// A lone separator followed by exactly three digits could be either; the report's language decides.
function parseMeasure(text: string, lang: string): Measure | null {
  const compact = text.replace(/−/g, '-').replace(/[\s\u00a0\u202f]/g, '');
  const unit = compact.match(UNIT)?.[0] ?? '';
  let s = compact.slice(0, compact.length - unit.length);
  if (!/^[+-]?\d[\d.,]*$/.test(s)) {
    return null;
  }
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  const commaDecimal = DECIMAL_COMMA.test(lang);
  if (lastDot >= 0 && lastComma >= 0) {
    const decimal = lastDot > lastComma ? '.' : ',';
    const group = decimal === '.' ? ',' : '.';
    s = s.split(group).join('').replace(decimal, '.');
  } else if (lastComma >= 0) {
    const single = s.indexOf(',') === lastComma;
    const decimals = s.length - lastComma - 1;
    const decimal = single && (decimals <= 2 || (decimals === 3 && commaDecimal));
    s = decimal ? s.replace(',', '.') : s.split(',').join('');
  } else if (lastDot >= 0) {
    const single = s.indexOf('.') === lastDot;
    const decimals = s.length - lastDot - 1;
    if (!single || (decimals === 3 && commaDecimal)) {
      s = s.split('.').join('');
    }
  }
  const value = Number(s);
  return /^[+-]?\d+(\.\d+)?$/.test(s) && Number.isFinite(value) ? { value, unit } : null;
}

/** Parses a number as people write it in a report: unit suffixes, spaces, either decimal separator. */
export function parseReportNumber(text: string, lang: string): number | null {
  return parseMeasure(text, lang)?.value ?? null;
}

const sameUnit = (measures: Measure[]): boolean =>
  measures.every((m) => m.unit === measures[0].unit);

interface DataRow {
  tr: HTMLTableRowElement;
  cells: string[];
}

const isHeaderRow = (tr: HTMLTableRowElement): boolean =>
  tr.cells.length > 0 && Array.from(tr.cells).every((c) => c.localName === 'th');

const readRows = (table: HTMLTableElement, minCells: number): DataRow[] | null => {
  const trs = Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody tr'));
  // Without a <thead>, the HTML parser puts the header row in the body.
  const firstData = trs.findIndex((tr) => !isHeaderRow(tr));
  const rows = trs.slice(firstData < 0 ? trs.length : firstData).map((tr) => ({
    tr,
    cells: Array.from(tr.cells).map((c) => (c.textContent ?? '').trim())
  }));
  if (
    rows.length === 0 ||
    rows.length > MAX_ROWS ||
    rows.some((r) =>
      r.cells.slice(1).some((c) => Array.from(c).length > MAX_VALUE_CHARS)
    ) ||
    rows.some((r) => r.cells.length < minCells)
  ) {
    return null;
  }
  return rows;
};

const esc = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const r2 = (n: number): number => Math.round(n * 100) / 100;

const clip = (text: string, max: number): string => {
  const chars = Array.from(text);
  return chars.length > max ? `${chars.slice(0, max - 1).join('')}…` : text;
};

const signed = (n: number, text: string): string => (n > 0 ? '+' : n < 0 ? '−' : '') + text;

const svgOpen = (width: number, height: number, label: string | null): string =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="100%" role="img"${
    label ? ` aria-label="${esc(label)}"` : ''
  }>`;

// Label column of width LABEL_W, then the plot; the viewBox is narrow so the text stays legible at phone width.
// The sub-line beside a change bar's name has room for this many characters; longer ones live in the hover title.
const SUBLINE_MAX = 28;
const CHANGE = { width: 520, zero: 330, half: 130, row: 44, top: 8, bar: 18 };

const renderChange = (
  table: HTMLTableElement,
  comparedTo: string | null,
  lang: string
): string | null => {
  const rows = readRows(table, 3);
  if (!rows) {
    return null;
  }
  const parsed: { row: DataRow; comparison: number; value: number }[] = [];
  for (const row of rows) {
    const comparison = parseMeasure(row.cells[1], lang);
    const value = parseMeasure(row.cells[2], lang);
    if (comparison === null || value === null || !sameUnit([comparison, value])) {
      return null;
    }
    parsed.push({ row, comparison: comparison.value, value: value.value });
  }
  const changes = parsed.map((p) =>
    p.comparison === 0 ? 0 : ((p.value - p.comparison) / Math.abs(p.comparison)) * 100
  );
  const scale = Math.max(100, Math.ceil(Math.max(...changes.map(Math.abs)) / 50) * 50);
  const { width, zero, half, row: rowH, top, bar } = CHANGE;
  const bottom = top + parsed.length * rowH;
  const px = (pct: number): number => r2((Math.abs(pct) / scale) * half);

  let out = svgOpen(width, bottom + 20, comparedTo) + '<g font-size="12">';
  for (const g of [-100, -50, 50, 100]) {
    const x = r2(zero + (g / scale) * half);
    out +=
      `<line x1="${x}" y1="4" x2="${x}" y2="${bottom}" stroke="var(--rule)" stroke-dasharray="3 4"/>` +
      `<text x="${x}" y="${bottom + 14}" text-anchor="middle" font-size="11" fill="var(--ink-3)">${signed(g, `${Math.abs(g)} %`)}</text>`;
  }
  out +=
    `<line x1="${zero}" y1="4" x2="${zero}" y2="${bottom}" stroke="var(--ink-3)"/>` +
    `<text x="${zero}" y="${bottom + 14}" text-anchor="middle" font-size="11" fill="var(--ink-3)">0 %</text>`;

  parsed.forEach(({ row, comparison, value }, i) => {
    const pct = changes[i];
    const y = top + i * rowH;
    const higherIsBetter = row.tr.getAttribute('data-direction') === 'higher-is-better';
    const unfavourable = higherIsBetter ? pct < 0 : pct > 0;
    const colour = unfavourable ? 'var(--bad)' : 'var(--good)';
    const label = row.cells[0];
    const pair = `${row.cells[1]} → ${row.cells[2]}`;
    out +=
      `<g><title>${esc(`${label}: ${pair}`)}</title>` +
      `<text x="0" y="${y + 17}" fill="var(--ink)" font-size="14" font-weight="650">${esc(clip(label, 15))}</text>` +
      (pair.length <= SUBLINE_MAX
        ? `<text x="0" y="${y + 33}" fill="var(--ink-3)" font-size="11">${esc(pair)}</text>`
        : '');
    const barY = y + 10;
    const textY = barY + bar - 4;
    if (comparison === 0) {
      const unit = row.cells[2].match(UNIT)?.[0];
      const delta = value - comparison;
      const text = signed(delta, `${r2(Math.abs(delta))}${unit ? ` ${unit}` : ''}`);
      out += `<text x="${zero + 6}" y="${textY}" fill="var(--ink-2)" font-size="12" font-weight="650">${esc(text)}</text>`;
    } else {
      const a = Math.abs(pct);
      const text = signed(pct, `${a >= 100 ? Math.round(a) : Math.round(a * 10) / 10} %`);
      const w = px(pct);
      if (w > 0) {
        const x = pct > 0 ? zero : r2(zero - w);
        out += `<rect x="${x}" y="${barY}" width="${w}" height="${bar}" fill="${colour}"/>`;
      }
      const anchor = pct < 0 ? 'end' : 'start';
      const tx = pct < 0 ? r2(zero - w - 6) : r2(zero + w + 6);
      out += `<text x="${tx}" y="${textY}" text-anchor="${anchor}" fill="${colour}" font-size="12" font-weight="650">${text}</text>`;
    }
    out += '</g>';
  });
  return `${out}</g></svg>`;
};

// Estimated glyph widths, used to keep text inside its column and inside the viewBox.
const TEXT_W = 6.6;
const NAME_W = 8.5;
const maxChars = (columnWidth: number, perChar: number): number =>
  Math.floor((columnWidth - 8) / perChar);

const BARS = { width: 520, left: 140, bar: 14 };

const renderBars = (
  table: HTMLTableElement,
  comparedTo: string | null,
  lang: string
): string | null => {
  const rows = readRows(table, 2);
  if (!rows) {
    return null;
  }
  const values: { row: DataRow; value: number; comparison: number | null }[] = [];
  const measures: Measure[] = [];
  for (const row of rows) {
    const value = parseMeasure(row.cells[1], lang);
    const hasComparison = row.cells.length > 2 && row.cells[2] !== '';
    const comparison = hasComparison ? parseMeasure(row.cells[2], lang) : null;
    if (
      value === null ||
      value.value < 0 ||
      (hasComparison && (comparison === null || comparison.value < 0))
    ) {
      return null;
    }
    measures.push(value, ...(comparison ? [comparison] : []));
    values.push({ row, value: value.value, comparison: comparison?.value ?? null });
  }
  if (!sameUnit(measures)) {
    return null;
  }
  const max = Math.max(
    1e-9,
    ...values.flatMap((v) => (v.comparison === null ? [v.value] : [v.value, v.comparison]))
  );
  const { width, left, bar } = BARS;
  const valueW =
    Math.max(
      ...values.flatMap((v) => [
        Array.from(v.row.cells[1]).length,
        Array.from(v.row.cells[2] ?? '').length
      ])
    ) * TEXT_W;
  const plot = Math.floor(width - left - 10 - valueW);
  const hasComparison = values.some((v) => v.comparison !== null);
  const rowH = hasComparison ? 44 : 30;
  const legend = hasComparison && comparedTo ? 22 : 0;

  let out = svgOpen(width, 8 + values.length * rowH + legend, null) + '<g font-size="12">';
  const series = (y: number, value: number, text: string, fill: string): string => {
    const w = r2((value / max) * plot);
    return (
      `<rect x="${left}" y="${y}" width="${w}" height="${bar}" fill="${fill}"/>` +
      `<text x="${r2(left + w + 6)}" y="${y + bar - 2}" fill="var(--ink-2)" font-size="12">${esc(text)}</text>`
    );
  };
  values.forEach(({ row, value, comparison }, i) => {
    const y = 8 + i * rowH;
    const label = row.cells[0];
    const title =
      comparison === null
        ? `${label}: ${row.cells[1]}`
        : `${label}: ${row.cells[2]} → ${row.cells[1]}`;
    out +=
      `<g><title>${esc(title)}</title>` +
      `<text x="0" y="${y + bar - 1}" fill="var(--ink)" font-size="14" font-weight="650">${esc(clip(label, maxChars(left, NAME_W)))}</text>` +
      series(y, value, row.cells[1], 'var(--brand)');
    if (comparison !== null) {
      out += series(y + bar + 4, comparison, row.cells[2], 'var(--parent)');
    }
    out += '</g>';
  });
  if (legend && comparedTo) {
    const y = 8 + values.length * rowH + 4;
    out +=
      `<rect x="${left}" y="${y}" width="10" height="10" fill="var(--parent)"/>` +
      `<text x="${left + 16}" y="${y + 9}" fill="var(--ink-2)" font-size="11">${esc(comparedTo)}</text>`;
  }
  return `${out}</g></svg>`;
};

const STACK = { width: 480, bar: 26, gap: 6 };
const SEGMENT_OPACITY = [1, 0.7, 0.45, 0.3];

const renderStacked = (table: HTMLTableElement, lang: string): string | null => {
  const rows = readRows(table, 2);
  if (!rows) {
    return null;
  }
  const items: { label: string; count: number; role: string; text: string }[] = [];
  for (const row of rows) {
    const count = parseReportNumber(row.cells[1], lang);
    if (count === null || count < 0) {
      return null;
    }
    const role = row.tr.getAttribute('data-role') === 'worse' ? 'worse' : 'review';
    items.push({ label: row.cells[0], count, role, text: row.cells[1] });
  }
  const total = items.reduce((sum, i) => sum + i.count, 0);
  if (total === 0) {
    return null;
  }
  const { width, bar, gap } = STACK;
  const seen = { review: 0, worse: 0 };
  const styled = items.map((item) => {
    const n = seen[item.role as 'review' | 'worse']++;
    return {
      ...item,
      fill: item.role === 'worse' ? 'var(--bad)' : 'var(--review)',
      opacity: SEGMENT_OPACITY[n % SEGMENT_OPACITY.length]
    };
  });
  const attrs = (s: (typeof styled)[number]): string =>
    `fill="${s.fill}"${s.opacity < 1 ? ` opacity="${s.opacity}"` : ''}`;

  let x = 0;
  let segments = '<g role="presentation">';
  for (const s of styled) {
    const w =
      s === styled[styled.length - 1] ? r2(width - x) : r2((s.count / total) * width);
    segments +=
      `<rect x="${r2(x)}" y="${gap}" width="${w}" height="${bar}" ${attrs(s)} stroke="var(--surface)">` +
      `<title>${esc(`${s.label}: ${s.text}`)}</title></rect>`;
    x += w;
  }
  segments += '</g>';

  let legend = '';
  let lx = 0;
  let ly = gap + bar + 18;
  for (const s of styled) {
    const count = ` · ${s.text}`;
    const text =
      clip(s.label, maxChars(width - 16, TEXT_W) - Array.from(count).length) + count;
    const itemW = 16 + Array.from(text).length * 6.6 + 14;
    if (lx > 0 && lx + itemW > width) {
      lx = 0;
      ly += 20;
    }
    legend +=
      `<rect x="${r2(lx)}" y="${ly - 9}" width="10" height="10" ${attrs(s)}/>` +
      `<text x="${r2(lx + 16)}" y="${ly}" fill="var(--ink-2)" font-size="12">${esc(text)}</text>`;
    lx += itemW;
  }
  return `${svgOpen(width, ly + 8, null)}${segments}${legend}</svg>`;
};

const METER = { left: 150, track: 220, bar: 12, row: 30 };

const renderMeter = (table: HTMLTableElement, lang: string): string | null => {
  const rows = readRows(table, 2);
  if (!rows) {
    return null;
  }
  const { left, track, bar, row: rowH } = METER;
  const valueW = Math.max(...rows.map((r) => Array.from(r.cells[1]).length)) * TEXT_W;
  const width = Math.ceil(left + track + 8 + valueW + 4);
  let out = svgOpen(width, 8 + rows.length * rowH, null) + '<g font-size="12">';
  for (const [i, row] of rows.entries()) {
    const measure = parseMeasure(row.cells[1], lang);
    const hasMax = row.cells.length > 2 && row.cells[2] !== '';
    const maxMeasure = hasMax ? parseMeasure(row.cells[2], lang) : null;
    if (
      measure === null ||
      (hasMax && (maxMeasure === null || !sameUnit([measure, maxMeasure])))
    ) {
      return null;
    }
    const value = measure.value;
    const max = maxMeasure?.value ?? 100;
    if (max <= 0 || value < 0 || value > max) {
      return null;
    }
    const y = 8 + i * rowH;
    const fill = r2((value / max) * track);
    out +=
      `<g><title>${esc(`${row.cells[0]}: ${row.cells[1]}`)}</title>` +
      `<text x="0" y="${y + bar}" fill="var(--ink)" font-size="14" font-weight="650">${esc(clip(row.cells[0], maxChars(left, NAME_W)))}</text>` +
      `<rect x="${left}" y="${y}" width="${track}" height="${bar}" fill="var(--rule)"/>` +
      `<rect x="${left}" y="${y}" width="${fill}" height="${bar}" fill="var(--brand)"/>` +
      `<text x="${left + track + 8}" y="${y + bar}" fill="var(--ink-2)" font-size="12">${esc(row.cells[1])}</text></g>`;
  }
  return `${out}</g></svg>`;
};

/** Draws a report data table as a static inline SVG, or returns null when the data does not fit the type. */
export function renderChart(
  type: ChartType,
  table: HTMLTableElement,
  labels: { comparedTo: string | null; lang: string }
): string | null {
  switch (type) {
    case 'change':
      return renderChange(table, labels.comparedTo, labels.lang);
    case 'bars':
      return renderBars(table, labels.comparedTo, labels.lang);
    case 'stacked':
      return renderStacked(table, labels.lang);
    case 'meter':
      return renderMeter(table, labels.lang);
  }
}
